import type React from "react";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type {
  GazeHeatmap360ViewerProps,
  GazePoint,
  HeatmapColorScheme,
  HeatmapViewMode,
  VisualizationMode,
} from "../../types/gazeHeatmapTypes";
import { DEFAULT_FIXATION_CONFIG } from "../../types/gazeHeatmapTypes";
import type { HotSpot } from "../../types/coverageStatistics";
import { useGazeHeatmapData, useSceneGazeHeatmapData } from "../../hooks/useGazeHeatmapData";
import {
  useHeatmapGeneration,
  useHeatmapColorScheme,
  useHeatmapOpacity,
  useScanpathConfig,
} from "../../hooks/useHeatmapGeneration";
import { useVideoSyncedHeatmap } from "../../hooks/useVideoSyncedHeatmap";
import { useCoverageStatistics } from "../../hooks/useCoverageStatistics";
import { useScanpathGeneration, useVideoSyncedScanpath } from "../../hooks/useScanpathGeneration";
import EquirectangularImage from "./EquirectangularImage";
import GazeHeatmapCanvas from "./GazeHeatmapCanvas";
import ScanpathCanvas from "./ScanpathCanvas";
import HeatmapLegend from "./HeatmapLegend";
import ScanpathLegend from "./ScanpathLegend";
import SegmentedControl from "src/common/components/SegmentedControl/SegmentedControl";
import RangeSelector from "src/common/components/RangeSelector/RangeSelector";
import InteractiveViewer360 from "./InteractiveViewer360";
import SceneTypeDropdown from "./SceneTypeDropdown";
import VideoControls from "./VideoControls";
import CoverageStatisticsPanel from "./CoverageStatisticsPanel";
import ScanpathSettings from "./ScanpathSettings";

/**
 * Main component for displaying 360° gaze heatmap visualization
 * Supports both individual session view and aggregated scene view
 */
const GazeHeatmap360Viewer: React.FC<GazeHeatmap360ViewerProps> = ({
  sessionId,
  contentId,
  sessionIds,
  groupMemberIds,
  heatmapOpacity: initialOpacity,
  colorScheme: initialColorScheme,
  onDataLoad,
}) => {
  const { t } = useTranslation();

  // State
  const [selectedSceneIndex, setSelectedSceneIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [viewMode, setViewMode] = useState<HeatmapViewMode>("flat");
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [showStatistics, setShowStatistics] = useState(false);
  const [visualizationMode, setVisualizationMode] = useState<VisualizationMode>("heatmap");
  const [hoveredHotSpot, setHoveredHotSpot] = useState<HotSpot | null>(null);
  // Scanpath config with localStorage persistence
  const [scanpathConfig, setScanpathConfig] = useScanpathConfig(DEFAULT_FIXATION_CONFIG);

  // Refs
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Persisted preferences
  const [colorScheme, setColorScheme] = useHeatmapColorScheme(initialColorScheme || "thermal");
  const [opacity, setOpacity] = useHeatmapOpacity(initialOpacity ?? 1.0);

  // Determine mode: individual session or aggregated scene
  const isAggregatedMode = !sessionId && !!contentId;

  // Fetch data based on mode
  const sessionQuery = useGazeHeatmapData({
    sessionId,
    contentId,
    enabled: !isAggregatedMode && !!sessionId,
  });

  const sceneQuery = useSceneGazeHeatmapData({
    contentId: contentId || 0,
    sessionIds,
    groupMemberIds,
    enabled: isAggregatedMode && !!contentId,
  });

  // Extract relevant data based on mode
  const { scenes, points, media, isLoading, error, sceneName, totalDuration } = useMemo(() => {
    if (isAggregatedMode) {
      const data = sceneQuery.data;
      return {
        scenes: null,
        points: data?.points || [],
        media: data?.sceneMedia,
        isLoading: sceneQuery.isLoading,
        error: sceneQuery.error?.message || null,
        sceneName: data?.sceneName || "",
        totalDuration: data?.sceneMedia?.duration || 0,
      };
    } else {
      const data = sessionQuery.data;
      const selectedScene = data?.scenes?.[selectedSceneIndex];
      return {
        scenes: data?.scenes || [],
        points: selectedScene?.points || [],
        media: selectedScene?.media,
        isLoading: sessionQuery.isLoading,
        error: sessionQuery.error?.message || null,
        sceneName: selectedScene?.sceneName || "",
        totalDuration: selectedScene?.duration || 0,
      };
    }
  }, [
    isAggregatedMode,
    sessionQuery.data,
    sessionQuery.isLoading,
    sessionQuery.error,
    sceneQuery.data,
    sceneQuery.isLoading,
    sceneQuery.error,
    selectedSceneIndex,
  ]);

  // Check if this is a video 360° scene based on sceneType from content_scene
  const isVideo360 = media?.sceneType === "video360";

  // Video crop times from content_scene (backend now sends in seconds)
  const videoTimeBegin = media?.timeBegin || 0;
  const videoTimeEnd = media?.timeEnd || media?.duration || 0;

  // For videos, the effective duration is the cropped segment
  // For images/other, use the scene duration from session data
  const videoDuration = isVideo360
    ? videoTimeEnd - videoTimeBegin
    : media?.duration || totalDuration || 0;

  // For images, use all points; for videos, the hook handles time-based filtering
  const filteredPoints: GazePoint[] = useMemo(() => {
    return points;
  }, [points]);

  // Memoize config to prevent infinite re-renders
  const heatmapConfig = useMemo(() => ({ colorScheme }), [colorScheme]);

  // Generate heatmap for images (with scene rotation offset for coordinate conversion)
  const {
    imageData: staticHeatmapImageData,
    isGenerating: isGeneratingStatic,
    progress: staticProgress,
    pointCount,
    processingTime,
  } = useHeatmapGeneration({
    points: filteredPoints,
    config: heatmapConfig,
    enabled: !isVideo360 && imageLoaded && filteredPoints.length > 0,
    sceneRotationY: media?.rotationY || 0,
  });

  // Calculate scene-relative time from video currentTime
  // Unity records points with Time.time (session time), backend converts to scene-relative (0, 0.5, 1...)
  // Video plays from timeBegin to timeEnd (e.g., 2 to 10)
  // So when video.currentTime = 2 (start), we want points at scene-relative time 0
  // sceneRelativeTime = videoCurrentTime - timeBegin
  const sceneRelativeTime = Math.max(0, videoCurrentTime - videoTimeBegin);

  // Generate video-synced heatmap for videos
  const {
    currentHeatmap: videoHeatmapImageData,
    isPrecomputing,
    precomputeProgress,
  } = useVideoSyncedHeatmap({
    points,
    videoDuration,
    currentTime: sceneRelativeTime,
    config: heatmapConfig,
    enabled: isVideo360 && points.length > 0,
    sceneRotationY: media?.rotationY || 0,
  });

  // Select the appropriate heatmap based on media type
  const heatmapImageData = isVideo360 ? videoHeatmapImageData : staticHeatmapImageData;
  const isGenerating = isVideo360 ? isPrecomputing : isGeneratingStatic;
  const progress = isVideo360 ? precomputeProgress : staticProgress;

  // Calculate coverage statistics from heatmap
  // IMPORTANT: Disable for videos to avoid performance issues (recalculating on every frame)
  const { statistics: coverageStats, isCalculating: isCalculatingStats } = useCoverageStatistics({
    imageData: heatmapImageData,
    enabled: !isVideo360 && !isGenerating && !!heatmapImageData,
  });

  // Generate scanpath from ALL points (computed once, not per-frame)
  const { scanpath, isGenerating: isGeneratingScanpath } = useScanpathGeneration({
    points: filteredPoints,
    config: scanpathConfig,
    sceneRotationY: media?.rotationY || 0,
    enabled:
      (visualizationMode === "scanpath" || visualizationMode === "both") &&
      filteredPoints.length > 0,
    isVideo360, // Use AVProVideo shader formula for video 360 content
  });

  // Video-synced scanpath: show ALL fixations from start up to current time
  // This displays the complete visual exploration path as the video progresses
  const { visibleFixations, visibleSaccades, currentFixation } = useVideoSyncedScanpath({
    scanpath,
    currentTime: sceneRelativeTime,
    historyDuration: sceneRelativeTime, // Show everything from time 0 to now
    enabled: isVideo360 && !!scanpath,
  });

  // For static images, show all fixations; for videos, show time-filtered fixations
  const displayFixations = isVideo360 ? visibleFixations : scanpath?.fixations || [];
  const displaySaccades = isVideo360 ? visibleSaccades : scanpath?.saccades || [];

  // Handle video time updates
  const handleTimeUpdate = useCallback((currentTime: number) => {
    setVideoCurrentTime(currentTime);
  }, []);

  // Report data load (including 0 when no data)
  useEffect(() => {
    if (!isLoading) {
      onDataLoad?.(points.length);
    }
  }, [points.length, isLoading, onDataLoad]);

  // Reset state when scene changes
  useEffect(() => {
    setImageLoaded(false);
    setVideoCurrentTime(0);
  }, [selectedSceneIndex, contentId]);

  // For 360 view mode, enable heatmap generation immediately since Three.js handles its own loading
  useEffect(() => {
    if (viewMode === "360" && !imageLoaded) {
      setImageLoaded(true);
    }
  }, [viewMode, imageLoaded]);

  // Auto-switch to 360° mode for videos since flat view can't display videos
  useEffect(() => {
    if (isVideo360 && viewMode === "flat") {
      setViewMode("360");
    }
  }, [isVideo360, viewMode]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600" />
        <p className="text-gray-500">{t("statsNew.gazeHeatmap.loadingData")}</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-12 w-12 text-red-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <p className="text-red-500">{t("statsNew.gazeHeatmap.fetchError")}</p>
      </div>
    );
  }

  // No media state
  if (!media || !media.url) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <p className="text-gray-500">{t("statsNew.gazeHeatmap.noMedia")}</p>
      </div>
    );
  }

  // No points state
  if (points.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </svg>
        <p className="text-gray-500">{t("statsNew.gazeHeatmap.noGazeData")}</p>
      </div>
    );
  }

  // Scene selector component
  const renderSceneSelector = () => {
    if (!scenes || scenes.length <= 1) return null;

    return (
      <div className="flex items-center gap-2 mb-4">
        <label className="text-sm text-gray-500">{t("statsNew.gazeHeatmap.scene")}:</label>
        <SceneTypeDropdown
          options={scenes.map((scene, index) => ({
            id: index,
            name: scene.sceneName,
            sceneType: scene.media?.sceneType,
            pointCount: scene.pointCount,
          }))}
          value={selectedSceneIndex}
          onChange={(id) => setSelectedSceneIndex(Number(id))}
          showPointCount
          className="min-w-[200px]"
        />
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header with scene selector (for session mode with multiple scenes) */}
      {renderSceneSelector()}

      {/* Controls row */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        {/* View mode toggle */}
        <div className="flex items-center gap-3">
          <SegmentedControl<HeatmapViewMode>
            options={[
              {
                value: "flat",
                label: t("statsNew.gazeHeatmap.viewFlat"),
                disabled: isGenerating || isVideo360,
              },
              { value: "360", label: t("statsNew.gazeHeatmap.view360"), disabled: isGenerating },
            ]}
            value={viewMode}
            onChange={setViewMode}
            variant="outlined"
          />

          {/* Visualization mode toggle */}
          <SegmentedControl<VisualizationMode>
            options={[
              {
                value: "heatmap",
                icon: (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z"
                    />
                  </svg>
                ),
              },
              {
                value: "scanpath",
                icon: (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                    />
                  </svg>
                ),
              },
              {
                value: "both",
                icon: (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                ),
              },
            ]}
            value={visualizationMode}
            onChange={setVisualizationMode}
            size="sm"
          />

          {/* Scanpath settings - only visible when scanpath is active */}
          {(visualizationMode === "scanpath" || visualizationMode === "both") && (
            <ScanpathSettings
              config={scanpathConfig}
              onChange={setScanpathConfig}
              disabled={isGeneratingScanpath}
            />
          )}
        </div>

        {/* Color scheme selector */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-500">{t("statsNew.gazeHeatmap.colorScheme")}:</label>
          <select
            value={colorScheme}
            onChange={(e) => setColorScheme(e.target.value as HeatmapColorScheme)}
            className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option value="green">{t("statsNew.gazeHeatmap.schemeGreen")}</option>
            <option value="thermal">{t("statsNew.gazeHeatmap.schemeThermal")}</option>
            <option value="viridis">{t("statsNew.gazeHeatmap.schemeViridis")}</option>
          </select>
        </div>

        {/* Opacity slider */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-500">{t("statsNew.gazeHeatmap.opacity")}:</label>
          <RangeSelector
            value={opacity}
            setValue={setOpacity}
            min={0}
            max={1}
            step={0.01}
            printValue={false}
            className="w-24 h-2 rounded-lg appearance-none cursor-pointer"
          />
          <span className="text-sm text-gray-600 w-8">{Math.round(opacity * 100)}%</span>
        </div>

        {/* Statistics toggle - disabled for videos (coverage stats not available) */}
        <button
          onClick={() => !isVideo360 && setShowStatistics(!showStatistics)}
          disabled={isVideo360}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
            isVideo360
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : showStatistics
              ? "bg-green-100 text-green-700"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
          title={isVideo360 ? t("statsNew.coverage.notAvailableForVideo") : undefined}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          {t("statsNew.coverage.stats")}
        </button>
      </div>

      {/* Main viewer area */}
      <div
        className="relative flex-1 bg-gray-100 rounded-lg overflow-hidden"
        style={{ aspectRatio: "2 / 1" }}
      >
        {/* Video 360 margin of error warning */}
        {isVideo360 && (
          <div
            className="absolute top-2 left-2 z-10 flex items-center gap-1.5 bg-black/60 text-white text-xs px-2 py-1 rounded"
            title={t("statsNew.coverage.video360MarginError")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{t("statsNew.coverage.video360MarginError")}</span>
          </div>
        )}

        {viewMode === "flat" ? (
          <>
            {/* Background 360° image (flat equirectangular view) */}
            <EquirectangularImage
              src={media.url}
              alt={sceneName}
              onLoad={() => setImageLoaded(true)}
            />

            {/* Heatmap overlay */}
            {(visualizationMode === "heatmap" || visualizationMode === "both") && (
              <GazeHeatmapCanvas imageData={heatmapImageData} opacity={opacity} />
            )}

            {/* Scanpath overlay (2D canvas) */}
            {(visualizationMode === "scanpath" || visualizationMode === "both") && (
              <ScanpathCanvas
                fixations={displayFixations}
                saccades={displaySaccades}
                currentFixation={currentFixation}
                opacity={opacity}
              />
            )}
          </>
        ) : (
          /* Interactive 360° viewer */
          /* For 360 videos, use preset cascade: preset2 (4K) → preset1 (2K) → raw URL */
          <InteractiveViewer360
            mediaUrl={isVideo360 ? media.urlPreset2 ?? media.urlPreset1 ?? media.url : media.url}
            mediaType={isVideo360 ? "video" : "image"}
            heatmapImageData={
              visualizationMode === "heatmap" || visualizationMode === "both"
                ? heatmapImageData
                : null
            }
            opacity={opacity}
            initialRotationY={media.rotationY || 0}
            videoRef={videoRef}
            onTimeUpdate={handleTimeUpdate}
            videoSegmentStart={isVideo360 ? videoTimeBegin : undefined}
            videoSegmentEnd={isVideo360 ? videoTimeEnd : undefined}
            showScanpath={visualizationMode === "scanpath" || visualizationMode === "both"}
            scanpathFixations={displayFixations}
            scanpathSaccades={displaySaccades}
            currentFixation={currentFixation}
            hoveredHotSpot={hoveredHotSpot}
          />
        )}

        {/* Hot spot hover indicator (flat view only) */}
        {hoveredHotSpot && viewMode === "flat" && (
          <div
            className="absolute pointer-events-none z-20"
            style={{
              left: `${hoveredHotSpot.center.u * 100}%`,
              top: `${(1 - hoveredHotSpot.center.v) * 100}%`,
              transform: "translate(-50%, -50%)",
            }}
          >
            {/* Pulsing circle */}
            <div className="relative">
              <div className="absolute inset-0 w-16 h-16 -ml-8 -mt-8 rounded-full border-4 border-red-500 animate-ping opacity-75" />
              <div className="absolute inset-0 w-16 h-16 -ml-8 -mt-8 rounded-full border-4 border-red-500" />
              <div className="absolute inset-0 w-8 h-8 -ml-4 -mt-4 rounded-full bg-red-500 opacity-30" />
            </div>
          </div>
        )}

        {/* Generation progress indicator */}
        {isGenerating && (
          <div className="absolute top-4 right-4 bg-white rounded-lg shadow px-3 py-2 flex items-center gap-2 z-10">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600" />
            <span className="text-sm text-gray-600">
              {isVideo360
                ? t("statsNew.gazeHeatmap.precomputing", { progress })
                : t("statsNew.gazeHeatmap.generating")}
            </span>
          </div>
        )}

        {/* Video controls - only for videos in 360° mode */}
        {isVideo360 && viewMode === "360" && (
          <div className="absolute bottom-4 left-4 right-4 z-10">
            <VideoControls
              videoRef={videoRef}
              currentTime={sceneRelativeTime}
              duration={videoDuration}
              segmentStart={videoTimeBegin}
            />
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="mt-4">
        {(visualizationMode === "heatmap" || visualizationMode === "both") && (
          <HeatmapLegend
            colorScheme={colorScheme}
            pointCount={filteredPoints.length}
            duration={totalDuration}
            coverageStats={coverageStats}
            showCoverageInline={!showStatistics}
          />
        )}
        {(visualizationMode === "scanpath" || visualizationMode === "both") && (
          <ScanpathLegend
            scanpath={scanpath}
            isLoading={isGeneratingScanpath}
            className={visualizationMode === "both" ? "mt-2" : ""}
          />
        )}
      </div>

      {/* Coverage Statistics Panel */}
      {showStatistics && (
        <div className="mt-4">
          <CoverageStatisticsPanel
            statistics={coverageStats}
            isLoading={isCalculatingStats}
            onHotSpotHover={setHoveredHotSpot}
          />
        </div>
      )}

      {/* Debug info (development only) */}
      {process.env.NODE_ENV === "development" && processingTime > 0 && (
        <div className="mt-2 text-xs text-gray-400">
          Processed {pointCount} points in {processingTime.toFixed(0)}ms
        </div>
      )}
    </div>
  );
};

export default GazeHeatmap360Viewer;
